import Chip, { ChipProps } from '@mui/material/Chip'
import { alpha, emphasize } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles<void, 'clickable' | 'outlined'>()(
  (theme, _params, classes) => ({
    colorInfo: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,

      [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
        backgroundColor: emphasize(theme.palette.info.main, 0.08),
      },

      [`&.${classes.outlined}`]: {
        backgroundColor: 'transparent',
        color: theme.palette.info.main,
        borderColor: theme.palette.info.main,

        [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
          backgroundColor: alpha(
            theme.palette.info.main,
            theme.palette.action.hoverOpacity,
          ),
        },
      },
    },
    colorWarning: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,

      [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
        backgroundColor: emphasize(theme.palette.warning.main, 0.08),
      },

      [`&.${classes.outlined}`]: {
        backgroundColor: 'transparent',
        color: theme.palette.warning.main,
        borderColor: theme.palette.warning.main,

        [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
          backgroundColor: alpha(
            theme.palette.warning.main,
            theme.palette.action.hoverOpacity,
          ),
        },
      },
    },
    colorError: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,

      [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
        backgroundColor: emphasize(theme.palette.error.main, 0.08),
      },

      [`&.${classes.outlined}`]: {
        backgroundColor: 'transparent',
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,

        [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
          backgroundColor: alpha(
            theme.palette.error.main,
            theme.palette.action.hoverOpacity,
          ),
        },
      },
    },
    colorSuccess: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,

      [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
        backgroundColor: emphasize(theme.palette.success.main, 0.08),
      },

      [`&.${classes.outlined}`]: {
        backgroundColor: 'transparent',
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,

        [`&.${classes.clickable}:hover, &.${classes.clickable}:focus`]: {
          backgroundColor: alpha(
            theme.palette.success.main,
            theme.palette.action.hoverOpacity,
          ),
        },
      },
    },
    // We need to generate a class for outlined to use it in other styles.
    outlined: {},
    clickable: {},
  }),
)

export type ColoredChipProps = Omit<ChipProps, 'color'> & {
  color?: ChipProps['color'] | 'info' | 'warning' | 'error' | 'success'
}

/** A `Chip` that supports info/warning/error/success colors. */
const ColoredChip: React.FC<React.PropsWithChildren<ColoredChipProps>> = ({
  color,
  ...props
}) => {
  const { classes, cx } = useStyles()

  let muiColorProp: ChipProps['color'] = 'default'

  if (color === 'primary' || color === 'secondary') {
    muiColorProp = color
  }

  return (
    <Chip
      {...props}
      className={cx({
        [classes.colorInfo]: color === 'info',
        [classes.colorWarning]: color === 'warning',
        [classes.colorError]: color === 'error',
        [classes.colorSuccess]: color === 'success',
        [classes.outlined]: props.variant === 'outlined',
        [classes.clickable]: props.clickable,
      })}
      color={muiColorProp}
    />
  )
}

export default ColoredChip
